/** @format */

import { CLOSE_LOADER, LOADER_OPEN } from "../Component/store/dialogue/dialogue.type";
import { baseURL, secretKey } from "./config";
import store from "../Component/store/Provider";
import { setToast } from "./toast";
import axios from "axios";

export const openSpinner = () => {
	return store.dispatch({ type: LOADER_OPEN });
};

export const closeSpinner = () => {
	return store.dispatch({ type: CLOSE_LOADER });
};

// Guard so we only show the session-expired toast / redirect once per
// burst of 401s (e.g. when several requests are in flight at the same time).
let isHandlingSessionExpired = false;

// Routes that already handle unauthenticated state themselves — we must NOT
// pop the "Session expired" toast or redirect again when a 401 happens here,
// otherwise users get bounced mid-login / mid-password-reset.
const UNAUTH_PATHS = ["/admin-login", "/register", "/forgotPassword", "/changePassword"];

function isOnAuthPage() {
	if (typeof window === "undefined") return false;
	const path = window.location.pathname || "";
	return UNAUTH_PATHS.some((p) => path === p || path.startsWith(p + "/"));
}

function hadActiveSession() {
	try {
		const t = sessionStorage.getItem("token");
		return !!(t && t !== "null" && t !== "undefined");
	} catch (_e) {
		return false;
	}
}

function handleSessionExpired() {
	if (isHandlingSessionExpired) return;

	// If we're already on a public/auth page, the 401 came from a background
	// check (e.g. AuthRoute's /login probe) — just ignore it.
	if (isOnAuthPage()) return;

	const hadSession = hadActiveSession();
	isHandlingSessionExpired = true;

	try {
		sessionStorage.clear();
		localStorage.clear();
		if (axios && axios.defaults && axios.defaults.headers && axios.defaults.headers.common) {
			axios.defaults.headers.common["key"] = "";
			axios.defaults.headers.common["Authorization"] = "";
		}
	} catch (e) {
		// ignore storage errors
	}

	// Only show the "Session expired" toast if the user actually had a token.
	// If they were never logged in, silently redirect — no confusing toast.
	if (hadSession) {
		setToast("error", "Session expired. Please log in again.");
	}

	// Give the toast a moment to render before redirecting to the login page.
	setTimeout(
		() => {
			if (typeof window !== "undefined" && window.location.pathname !== "/admin-login") {
				window.location.href = "/admin-login";
			}
		},
		hadSession ? 1200 : 0,
	);
}

// ── Spurious-401 debounce ─────────────────────────────────────────────────
// Admins were being logged out at seemingly random times. The admin JWT is
// valid for 8h, so an occasional single 401 (a transient gateway/cold-start
// blip, a momentary cookie/header race, or one misbehaving endpoint) should
// NOT destroy an otherwise-valid session. We only treat the session as expired
// after TWO 401s within a short window: a genuinely expired/invalid token 401s
// on virtually every request, so logout still happens within a second or two,
// while a one-off blip is ignored.
let pending401Count = 0;
let pending401Timer = null;
const SESSION_EXPIRY_401_THRESHOLD = 2;
const SESSION_EXPIRY_401_WINDOW_MS = 10000;

function noteUnauthorized() {
	// Background probes on auth pages already handled by handleSessionExpired.
	if (isOnAuthPage()) return;

	// Never-logged-in users: no session to protect — redirect immediately.
	if (!hadActiveSession()) {
		handleSessionExpired();
		return;
	}

	pending401Count += 1;
	if (pending401Count >= SESSION_EXPIRY_401_THRESHOLD) {
		if (pending401Timer) {
			clearTimeout(pending401Timer);
			pending401Timer = null;
		}
		pending401Count = 0;
		handleSessionExpired();
		return;
	}
	if (!pending401Timer) {
		pending401Timer = setTimeout(() => {
			pending401Count = 0;
			pending401Timer = null;
		}, SESSION_EXPIRY_401_WINDOW_MS);
	}
}

function handleErrors(response) {
	if (response && response.status === 401) {
		noteUnauthorized();
		throw new Error("Session expired. Please log in again.");
	}
	if (!response.ok) {
		throw new Error(`HTTP error! Status: ${response?.status}`);
	}
	return response.json();
}

export { handleSessionExpired, noteUnauthorized };
export const apiInstanceFetch = {
	baseURL: `${baseURL}`, // Set your default base URL here
	headers: {
		"Content-Type": "application/json",
		key: `${secretKey}`,
	},
	get: async (url) => {
		openSpinner();
		return fetch(`${apiInstanceFetch?.baseURL}${url}`, {
			method: "GET",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				key: `${secretKey}`,
				Authorization: `${sessionStorage.getItem("token")}`, // dynamically fetch the token from sessionStorage
			},
		})
			.then(handleErrors)
			.finally(() => closeSpinner());
	},

	post: (url, data) => {
		openSpinner();
		return fetch(`${apiInstanceFetch.baseURL}${url}`, {
			method: "POST",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				key: `${secretKey}`,
				Authorization: `${sessionStorage.getItem("token")}`, // dynamically fetch the token from sessionStorage
			},
			body: data,
		})
			.then(handleErrors)
			.finally(() => closeSpinner());
	},

	patch: (url, data) => {
		openSpinner();
		return fetch(`${apiInstanceFetch.baseURL}${url}`, {
			method: "PATCH",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				key: `${secretKey}`,
				Authorization: `${sessionStorage.getItem("token")}`, // dynamically fetch the token from sessionStorage
			},
			body: JSON.stringify(data),
		})
			.then(handleErrors)
			.finally(() => closeSpinner());
	},

	put: (url, data) => {
		openSpinner();
		return fetch(`${apiInstanceFetch.baseURL}${url}`, {
			method: "PUT",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				key: `${secretKey}`,
				Authorization: `${sessionStorage.getItem("token")}`, // dynamically fetch the token from sessionStorage
			},
			body: JSON.stringify(data),
		})
			.then(handleErrors)
			.finally(() => closeSpinner());
	},

	delete: (url) => {
		openSpinner();
		return fetch(`${apiInstanceFetch.baseURL}${url}`, {
			method: "DELETE",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				key: `${secretKey}`,
				Authorization: `${sessionStorage.getItem("token")}`, // dynamically fetch the token from sessionStorage
			},
		})
			.then(handleErrors)
			.finally(() => closeSpinner());
	},
};
